import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  Mobiles,
  Headphones,
  Airpods,
  Keyboards,
} from "../../Data/ProductData";

import ProductCard from "./ProductCard";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  ChevronDownIcon,
  FunnelIcon,
  MinusIcon,
  PlusIcon,
  Squares2X2Icon,
} from "@heroicons/react/20/solid";
import { useDispatch } from "react-redux";
import { findProducts } from "../../../State/Product/Action";

const sortOptions = [
  { name: "Price: Low to High", href: "#", current: false },
  { name: "Price: High to Low", href: "#", current: false },
];

const filters = [
  {
    id: "brands",
    name: "Brands",
    options: [
      { value: "Samsung", label: "Samsung", checked: false },
      { value: "Apple", label: "Apple", checked: false },
      { value: "Google", label: "Google", checked: false },
      { value: "Xiaomi", label: "Xiaomi", checked: false },
      { value: "Beats", label: "Beats", checked: false },
    ],
  },
  {
    id: "subcategories",
    name: "Subcategories",
    options: [
      { value: "Smartphones", label: "Smartphones", checked: false },
      { value: "Tablets", label: "Tablets", checked: false },
      { value: "Laptops", label: "Laptops", checked: false },
      { value: "Wearables", label: "Wearables", checked: false },
      { value: "Accessories", label: "Accessories", checked: false },
    ],
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function Product() {
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const param = useParams();
  const dispatch = useDispatch();

  const handleFilter = (value, sectionId) => {
    const searchParams = new URLSearchParams(location.search);
    let filterValue = searchParams.getAll(sectionId);

    // Check if the filterValue array exists and contains the value
    if (filterValue.length > 0) {
      // Split the first value by comma to get individual selected values
      let currentValues = filterValue[0].split(",");

      // If the value is already present, remove it
      if (currentValues.includes(value)) {
        currentValues = currentValues.filter((item) => item !== value);
      } else {
        // If the value is not present, add it
        currentValues.push(value);
      }

      // Update the search parameters
      if (currentValues.length > 0) {
        searchParams.set(sectionId, currentValues.join(","));
      } else {
        searchParams.delete(sectionId);
      }
    } else {
      // If no filters exist, add the new value
      searchParams.set(sectionId, value);
    }

    // Update the URL with the new search parameters
    const query = searchParams.toString();
    navigate({ search: `?${query}` });
  };

  const decodedQueryString = decodeURIComponent(location.search);
  const searchParams = new URLSearchParams(decodedQueryString);
  const colorValue = searchParams.get("color");
  const priceValue = searchParams.get("price");
  const discountValue = searchParams.get("discount");
  const sortValue = searchParams.get("sort");
  const categoryValue = searchParams.get("category");
  const brandValue = searchParams.get("brand");
  const pageNumber = searchParams.get("page") || 1;
  const stock = searchParams.get("stock");

  const allProducts = [
    ...(Airpods || []),
    ...(Mobiles || []),
    ...(Keyboards || []),
    ...(Headphones || []),
  ];

  useEffect(() => {
    const [minPrice, maxPrice] =
      priceValue === null ? [0, 10000] : priceValue.split("-").map(Number);
    const data = {
      color: colorValue || [],
      category: param.levelThree,
      minPrice,
      maxPrice,
      sort: sortValue || "price_low",
      pageNumber: pageNumber - 1,
      pageSize: 10,
      stock: stock,
    };
    dispatch(findProducts(data));
  }, [
    param.levelThree,
    colorValue,
    priceValue,
    discountValue,
    sortValue,
    categoryValue,
    brandValue,
    stock,
    pageNumber,
  ]);

  return (
    <div className="bg-white">
      <div>
        {/* Mobile filter dialog */}
        <Dialog
          open={mobileFiltersOpen}
          onClose={setMobileFiltersOpen}
          className="relative z-40 lg:hidden"
        >
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-black bg-opacity-25 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
          />

          <div className="fixed inset-0 z-40 flex">
            <DialogPanel
              transition
              className="relative ml-auto flex h-full w-full max-w-xs transform flex-col overflow-y-auto bg-white py-4 pb-12 shadow-xl transition duration-300 ease-in-out data-[closed]:translate-x-full"
            >
              <div className="flex items-center justify-between px-4">
                <h2 className="text-lg font-medium text-gray-900">Filters</h2>
                <button
                  type="button"
                  onClick={() => setMobileFiltersOpen(false)}
                  className="-mr-2 flex h-10 w-10 items-center justify-center rounded-md bg-white p-2 text-gray-400"
                >
                  <span className="sr-only">Close menu</span>
                  <XMarkIcon aria-hidden="true" className="h-6 w-6" />
                </button>
              </div>

              {/* Filters */}
              <form className="mt-4 border-t border-gray-200">
                {/* <h3 className="">Filters</h3> */}

                {filters.map((section) => (
                  <Disclosure
                    key={section.id}
                    as="div"
                    className="border-t border-gray-200 px-4 py-6"
                    defaultOpen
                  >
                    <h3 className="-mx-2 -my-3 flow-root">
                      <DisclosureButton className="group flex w-full items-center justify-between bg-white px-2 py-3 text-gray-400 hover:text-gray-500">
                        <span className="font-medium text-gray-900">
                          {section.name}
                        </span>
                        <span className="ml-6 flex items-center">
                          <PlusIcon
                            aria-hidden="true"
                            className="h-5 w-5 group-data-[open]:hidden"
                          />
                          <MinusIcon
                            aria-hidden="true"
                            className="h-5 w-5 [.group:not([data-open])_&]:hidden"
                          />
                        </span>
                      </DisclosureButton>
                    </h3>
                    <DisclosurePanel className="pt-6">
                      <div className="space-y-6">
                        {section.options.map((option, optionIdx) => (
                          <div key={option.value} className="flex items-center">
                            <input
                              onChange={() =>
                                handleFilter(option.value, section.id)
                              }
                              defaultValue={option.value}
                              defaultChecked={option.checked}
                              id={`filter-mobile-${section.id}-${optionIdx}`}
                              name={`${section.id}[]`}
                              type="checkbox"
                              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <label
                              htmlFor={`filter-mobile-${section.id}-${optionIdx}`}
                              className="ml-3 min-w-0 flex-1 text-gray-500"
                            >
                              {option.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </DisclosurePanel>
                  </Disclosure>
                ))}
              </form>
            </DialogPanel>
          </div>
        </Dialog>

        <main className="mx-auto margin-x">
          <div className="flex items-center  justify-between border-b rounded-2xl bg-[#1E1E1E] border-gray-200 py-4 px-4 ">
            <h1 className="text-xl md:text-2xl font-bold tracking-tight text-white">
              Product Catalogue
            </h1>

            <div className="flex items-center">
              <Menu as="div" className="relative inline-block text-left">
                <div>
                  <MenuButton className="group inline-flex justify-center text-sm font-medium text-white hover:text-gray-200">
                    Sort
                    <ChevronDownIcon
                      aria-hidden="true"
                      className="-mr-1 ml-1 h-5 w-5 flex-shrink-0 text-white group-hover:text-gray-200"
                    />
                  </MenuButton>
                </div>

                <MenuItems
                  transition
                  className="absolute right-0 z-10 mt-2 w-40 origin-top-right rounded-md bg-white shadow-2xl ring-1 ring-black ring-opacity-5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                >
                  <div className="py-1">
                    {sortOptions.map((option) => (
                      <MenuItem key={option.name}>
                        <a
                          href={option.href}
                          className={classNames(
                            option.current
                              ? "font-medium text-gray-900"
                              : "text-gray-500",
                            "block px-4 py-2 text-sm data-[focus]:bg-gray-100"
                          )}
                        >
                          {option.name}
                        </a>
                      </MenuItem>
                    ))}
                  </div>
                </MenuItems>
              </Menu>

              <button
                type="button"
                className="-m-2 ml-5 p-2 text-white hover:text-gray-200 sm:ml-7"
              >
                <span className="sr-only">View grid</span>
                <Squares2X2Icon aria-hidden="true" className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={() => setMobileFiltersOpen(true)}
                className="-m-2 ml-4 p-2 text-gray-400 hover:text-gray-500 sm:ml-6 lg:hidden"
              >
                <span className="sr-only">Filters</span>
                <FunnelIcon aria-hidden="true" className="h-5 w-5" />
              </button>
            </div>
          </div>

          <section aria-labelledby="products-heading" className="pb-24 pt-6">
            <h2 id="products-heading" className="sr-only">
              Products
            </h2>

            <div className="grid grid-cols-1 gap-x-6 gap-y-6 lg:grid-cols-5">
              {/* Filters */}
              <form className="hidden h-screen p-4 bg-[#1E1E1E] text-white rounded-2xl lg:block">
                <h3 className="pb-4 text-2xl font-bold">Filters</h3>

                {filters.map((section) => (
                  <Disclosure
                    key={section.id}
                    as="div"
                    className="border-b border-gray-200 py-6"
                    defaultOpen
                  >
                    <h3 className="-my-3 flow-root">
                      <DisclosureButton className="group flex w-full items-center justify-between bg-[#1E1E1E] py-3 text-sm text-white hover:text-gray-200">
                        <span className="font-bold text-xl text-white">
                          {section.name}
                        </span>
                        <span className="ml-6 flex items-center">
                          <PlusIcon
                            aria-hidden="true"
                            className="h-5 w-5 group-data-[open]:hidden"
                          />
                          <MinusIcon
                            aria-hidden="true"
                            className="h-5 w-5 [.group:not([data-open])_&]:hidden"
                          />
                        </span>
                      </DisclosureButton>
                    </h3>
                    <DisclosurePanel className="pt-6">
                      <div className="space-y-4">
                        {section.options.map((option, optionIdx) => (
                          <div key={option.value} className="flex items-center">
                            <input
                              onChange={() =>
                                handleFilter(option.value, section.id)
                              }
                              defaultValue={option.value}
                              defaultChecked={option.checked}
                              id={`filter-${section.id}-${optionIdx}`}
                              name={`${section.id}[]`}
                              type="checkbox"
                              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <label
                              htmlFor={`filter-${section.id}-${optionIdx}`}
                              className="ml-3 text-lg text-white"
                            >
                              {option.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </DisclosurePanel>
                  </Disclosure>
                ))}
              </form>

              {/* Product grid */}
              <div className="col-span-3 md:col-span-4 w-full">
                <div className="flex flex-wrap justify-center py-5">
                  {allProducts.map((product) =>
                    product ? (
                      <ProductCard key={product.id} product={product} />
                    ) : null
                  )}
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}

// import { useEffect, useState } from "react";
// import { useLocation, useNavigate, useParams } from "react-router-dom";

// import {
//   Mobiles,
//   Headphones,
//   Airpods,
//   Keyboards,
// } from "../../Data/ProductData";

// import ProductCard from "./ProductCard";
// import {
//   Dialog,
//   DialogBackdrop,
//   DialogPanel,
//   Disclosure,
//   DisclosureButton,
//   DisclosurePanel,
//   Menu,
//   MenuButton,
//   MenuItem,
//   MenuItems,
// } from "@headlessui/react";
// import { XMarkIcon } from "@heroicons/react/24/outline";
// import {
//   ChevronDownIcon,
//   FunnelIcon,
//   MinusIcon,
//   PlusIcon,
//   Squares2X2Icon,
// } from "@heroicons/react/20/solid";
// import { useDispatch } from "react-redux";
// import { findProducts } from "../../../State/Product/Action";

// const sortOptions = [
//   { name: "Price: Low to High", href: "#", current: false },
//   { name: "Price: High to Low", href: "#", current: false },
// ];

// const filters = [
//   {
//     id: "brands",
//     name: "Brands",
//     options: [
//       { value: "Samsung", label: "Samsung", checked: false },
//       { value: "Apple", label: "Apple", checked: false },
//       { value: "Google", label: "Google", checked: false },
//       { value: "Xiaomi", label: "Xiaomi", checked: false },
//       { value: "Beats", label: "Beats", checked: false },
//     ],
//   },
//   {
//     id: "subcategories",
//     name: "Subcategories",
//     options: [
//       { value: "Smartphones", label: "Smartphones", checked: false },
//       { value: "Tablets", label: "Tablets", checked: false },
//       { value: "Laptops", label: "Laptops", checked: false },
//       { value: "Wearables", label: "Wearables", checked: false },
//       { value: "Accessories", label: "Accessories", checked: false },
//     ],
//   },
// ];

// function classNames(...classes) {
//   return classes.filter(Boolean).join(" ");
// }

// export default function Product() {
//   const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
//   const location = useLocation();
//   const navigate = useNavigate();
//   const param = useParams();
//   const dispatch = useDispatch();

//   const handleFilter = (value, sectionId) => {
//     const searchParams = new URLSearchParams(location.search);
//     let filterValue = searchParams.getAll(sectionId);

//     // Check if the filterValue array exists and contains the value
//     if (filterValue.length > 0) {
//       // Split the first value by comma to get individual selected values
//       let currentValues = filterValue[0].split(",");

//       // If the value is already present, remove it
//       if (currentValues.includes(value)) {
//         currentValues = currentValues.filter((item) => item !== value);
//       } else {
//         // If the value is not present, add it
//         currentValues.push(value);
//       }

//       // Update the search parameters
//       if (currentValues.length > 0) {
//         searchParams.set(sectionId, currentValues.join(","));
//       } else {
//         searchParams.delete(sectionId);
//       }
//     } else {
//       // If no filters exist, add the new value
//       searchParams.set(sectionId, value);
//     }

//     // Update the URL with the new search parameters
//     const query = searchParams.toString();
//     navigate({ search: `?${query}` });
//   };

//   const decodedQueryString = decodeURIComponent(location.search);
//   const searchParams = new URLSearchParams(decodedQueryString);
//   const colorValue = searchParams.get("color");
//   const priceValue = searchParams.get("price");
//   const discountValue = searchParams.get("discount");
//   const sortValue = searchParams.get("sort");
//   const categoryValue = searchParams.get("category");
//   const brandValue = searchParams.get("brand");
//   const pageNumber = searchParams.get("page") || 1;
//   const stock = searchParams.get("stock");

//   const allProducts = [
//     ...(Airpods || []),
//     ...(Mobiles || []),
//     ...(Keyboards || []),
//     ...(Headphones || []),
//   ];

//   useEffect(() => {
//     const [minPrice, maxPrice] =
//       priceValue === null ? [0, 10000] : priceValue.split("-").map(Number);
//     const data = {
//       color: colorValue || [],
//       category: param.levelThree,
//       minPrice,
//       maxPrice,
//       sort: sortValue || "price_low",
//       pageNumber: pageNumber - 1,
//       pageSize: 10,
//       stock: stock,
//     };
//     dispatch(findProducts(data));
//   }, [
//     param.levelThree,
//     colorValue,
//     priceValue,
//     discountValue,
//     sortValue,
//     categoryValue,
//     brandValue,
//     stock,
//     pageNumber,
//   ]);

//   return (
//     <div className="bg-white">
//       <div>
//         {/* Mobile filter dialog */}
//         <Dialog
//           open={mobileFiltersOpen}
//           onClose={setMobileFiltersOpen}
//           className="relative z-40 lg:hidden"
//         >
//           <DialogBackdrop
//             transition
//             className="fixed inset-0 bg-black bg-opacity-25 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
//           />

//           <div className="fixed inset-0 z-40 flex">
//             <DialogPanel
//               transition
//               className="relative ml-auto flex h-full w-full max-w-xs transform flex-col overflow-y-auto bg-white py-4 pb-12 shadow-xl transition duration-300 ease-in-out data-[closed]:translate-x-full"
//             >
//               <div className="flex items-center justify-between px-4">
//                 <h2 className="text-lg font-medium text-gray-900">Filters</h2>
//                 <button
//                   type="button"
//                   onClick={() => setMobileFiltersOpen(false)}
//                   className="-mr-2 flex h-10 w-10 items-center justify-center rounded-md bg-white p-2 text-gray-400"
//                 >
//                   <span className="sr-only">Close menu</span>
//                   <XMarkIcon aria-hidden="true" className="h-6 w-6" />
//                 </button>
//               </div>

//               {/* Filters */}
//               <form className="mt-4 border-t border-gray-200">
//                 {/* <h3 className="">Filters</h3> */}

//                 {filters.map((section) => (
//                   <Disclosure
//                     key={section.id}
//                     as="div"
//                     className="border-t border-gray-200 px-4 py-6"
//                     defaultOpen
//                   >
//                     <h3 className="-mx-2 -my-3 flow-root">
//                       <DisclosureButton className="group flex w-full items-center justify-between bg-white px-2 py-3 text-gray-400 hover:text-gray-500">
//                         <span className="font-medium text-gray-900">
//                           {section.name}
//                         </span>
//                         <span className="ml-6 flex items-center">
//                           <PlusIcon
//                             aria-hidden="true"
//                             className="h-5 w-5 group-data-[open]:hidden"
//                           />
//                           <MinusIcon
//                             aria-hidden="true"
//                             className="h-5 w-5 [.group:not([data-open])_&]:hidden"
//                           />
//                         </span>
//                       </DisclosureButton>
//                     </h3>
//                     <DisclosurePanel className="pt-6">
//                       <div className="space-y-6">
//                         {section.options.map((option, optionIdx) => (
//                           <div key={option.value} className="flex items-center">
//                             <input
//                               onChange={() =>
//                                 handleFilter(option.value, section.id)
//                               }
//                               defaultValue={option.value}
//                               defaultChecked={option.checked}
//                               id={`filter-mobile-${section.id}-${optionIdx}`}
//                               name={`${section.id}[]`}
//                               type="checkbox"
//                               className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
//                             />
//                             <label
//                               htmlFor={`filter-mobile-${section.id}-${optionIdx}`}
//                               className="ml-3 min-w-0 flex-1 text-gray-500"
//                             >
//                               {option.label}
//                             </label>
//                           </div>
//                         ))}
//                       </div>
//                     </DisclosurePanel>
//                   </Disclosure>
//                 ))}
//               </form>
//             </DialogPanel>
//           </div>
//         </Dialog>

//         <main className="mx-auto margin-x">
//           <div className="flex items-center  justify-between border-b rounded-2xl bg-[#1E1E1E] border-gray-200 py-4 px-4 ">
//             <h1 className="text-xl md:text-2xl font-bold tracking-tight text-white">
//               Product Catalogue
//             </h1>

//             <div className="flex items-center">
//               <Menu as="div" className="relative inline-block text-left">
//                 <div>
//                   <MenuButton className="group inline-flex justify-center text-sm font-medium text-white hover:text-gray-200">
//                     Sort
//                     <ChevronDownIcon
//                       aria-hidden="true"
//                       className="-mr-1 ml-1 h-5 w-5 flex-shrink-0 text-white group-hover:text-gray-200"
//                     />
//                   </MenuButton>
//                 </div>

//                 <MenuItems
//                   transition
//                   className="absolute right-0 z-10 mt-2 w-40 origin-top-right rounded-md bg-white shadow-2xl ring-1 ring-black ring-opacity-5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
//                 >
//                   <div className="py-1">
//                     {sortOptions.map((option) => (
//                       <MenuItem key={option.name}>
//                         <a
//                           href={option.href}
//                           className={classNames(
//                             option.current
//                               ? "font-medium text-gray-900"
//                               : "text-gray-500",
//                             "block px-4 py-2 text-sm data-[focus]:bg-gray-100"
//                           )}
//                         >
//                           {option.name}
//                         </a>
//                       </MenuItem>
//                     ))}
//                   </div>
//                 </MenuItems>
//               </Menu>

//               <button
//                 type="button"
//                 className="-m-2 ml-5 p-2 text-white hover:text-gray-200 sm:ml-7"
//               >
//                 <span className="sr-only">View grid</span>
//                 <Squares2X2Icon aria-hidden="true" className="h-5 w-5" />
//               </button>
//               <button
//                 type="button"
//                 onClick={() => setMobileFiltersOpen(true)}
//                 className="-m-2 ml-4 p-2 text-gray-400 hover:text-gray-500 sm:ml-6 lg:hidden"
//               >
//                 <span className="sr-only">Filters</span>
//                 <FunnelIcon aria-hidden="true" className="h-5 w-5" />
//               </button>
//             </div>
//           </div>

//           <section aria-labelledby="products-heading" className="pb-24 pt-6">
//             <h2 id="products-heading" className="sr-only">
//               Products
//             </h2>

//             <div className="grid grid-cols-1 gap-x-6 gap-y-6 lg:grid-cols-5">
//               {/* Filters */}
//               <form className="hidden h-screen p-4 bg-[#1E1E1E] text-white rounded-2xl lg:block">
//                 <h3 className="pb-4 text-2xl font-bold">Filters</h3>

//                 {filters.map((section) => (
//                   <Disclosure
//                     key={section.id}
//                     as="div"
//                     className="border-b border-gray-200 py-6"
//                     defaultOpen
//                   >
//                     <h3 className="-my-3 flow-root">
//                       <DisclosureButton className="group flex w-full items-center justify-between bg-[#1E1E1E] py-3 text-sm text-white hover:text-gray-200">
//                         <span className="font-bold text-xl text-white">
//                           {section.name}
//                         </span>
//                         <span className="ml-6 flex items-center">
//                           <PlusIcon
//                             aria-hidden="true"
//                             className="h-5 w-5 group-data-[open]:hidden"
//                           />
//                           <MinusIcon
//                             aria-hidden="true"
//                             className="h-5 w-5 [.group:not([data-open])_&]:hidden"
//                           />
//                         </span>
//                       </DisclosureButton>
//                     </h3>
//                     <DisclosurePanel className="pt-6">
//                       <div className="space-y-4">
//                         {section.options.map((option, optionIdx) => (
//                           <div key={option.value} className="flex items-center">
//                             <input
//                               onChange={() =>
//                                 handleFilter(option.value, section.id)
//                               }
//                               defaultValue={option.value}
//                               defaultChecked={option.checked}
//                               id={`filter-${section.id}-${optionIdx}`}
//                               name={`${section.id}[]`}
//                               type="checkbox"
//                               className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
//                             />
//                             <label
//                               htmlFor={`filter-${section.id}-${optionIdx}`}
//                               className="ml-3 text-lg text-white"
//                             >
//                               {option.label}
//                             </label>
//                           </div>
//                         ))}
//                       </div>
//                     </DisclosurePanel>
//                   </Disclosure>
//                 ))}
//               </form>

//               {/* Product grid */}
//               <div className="col-span-3 md:col-span-4 w-full">
//                 <div className="flex flex-wrap justify-center py-5">
//                   {allProducts.map((product) =>
//                     product ? (
//                       <ProductCard key={product.id} product={product} />
//                     ) : null
//                   )}
//                 </div>
//               </div>
//             </div>
//           </section>
//         </main>
//       </div>
//     </div>
//   );
// }
